import { useCallback } from "react";

import { useNavigate, useRouter } from "@tanstack/react-router";
import { useLogoutMutation } from "../../../api/auth";
import { clearStore, useStore } from "../../../store";
import { tokenStorage } from "../../../utils/tokenStorage";
import { useToast } from "../../ui";

export const useAppLayout = () => {
  const { toast } = useToast();

  const router = useRouter();
  const navigate = useNavigate();

  const { resetAuthStore, resetGuidance } = useStore();

  const { mutateAsync } = useLogoutMutation({
    onError: (error) => {
      toast({
        variant: "destructive",
        title: error?.message ?? "Logout failed",
      });
    },
  });

  const handleLogout = useCallback(async () => {
    // Attempt logout API call, but don't block local cleanup on failure
    try {
      await mutateAsync();
    } catch {
      // Error toast is already handled by onError callback
      // Continue with local logout even if API call failed
    }

    // Always clear cookies as backup (resetAuthStore also clears them)
    tokenStorage.clearAuthCookies();

    // Clear persisted store from sessionStorage
    clearStore();

    // Reset guidance state so the next session cannot show stale in-memory progress
    resetGuidance();

    // Reset auth store state (also clears cookies)
    // This ensures users are logged out locally even if server-side logout fails
    resetAuthStore();

    // invalidate router and finally navigate to login page
    router
      .invalidate()
      .finally(() => {
        void navigate({ to: "/auth/login", search: { from: undefined } });
      })
      .catch((error: unknown) => {
        console.error("[app-layout] Failed to redirect after logout", { error });
      });
  }, [resetAuthStore, resetGuidance, router, mutateAsync, navigate]);

  return {
    handleLogout,
  };
};
